import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, Form, Pagination, Row, Spinner, Table, Alert } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { deletePingLog, getPingLogs, PingLogListItem, uploadSecureCRTPingLog } from './pingLogService';
import './PingLog.css';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

const PingLogListPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [items, setItems] = useState<PingLogListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [deviceName, setDeviceName] = useState(searchParams.get('deviceName') || '');
    const [targetIp, setTargetIp] = useState(searchParams.get('targetIp') || '');
    const [sourceIp, setSourceIp] = useState(searchParams.get('sourceIp') || '');

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadSuccess, setUploadSuccess] = useState('');

    const [deletingLogId, setDeletingLogId] = useState('');

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const page = Number(searchParams.get('page') || DEFAULT_PAGE);
    const pageSize = Number(searchParams.get('pageSize') || DEFAULT_PAGE_SIZE);

    useEffect(() => {
        const params: Record<string, string> = {
            page: String(page),
            pageSize: String(pageSize),
        };

        if (deviceName.trim()) params.deviceName = deviceName.trim();
        if (targetIp.trim()) params.targetIp = targetIp.trim();
        if (sourceIp.trim()) params.sourceIp = sourceIp.trim();

        setSearchParams(params);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totalPages = useMemo(() => {
        if (!total || !pageSize) return 1;
        return Math.max(1, Math.ceil(total / pageSize));
    }, [total, pageSize]);

    const loadData = async () => {
        try {
            setLoading(true);
            setError('');

            const data = await getPingLogs({
                page,
                pageSize,
                deviceName: deviceName || undefined,
                targetIp: targetIp || undefined,
                sourceIp: sourceIp || undefined,
            });

            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load ping logs.');
            setItems([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, deviceName, targetIp, sourceIp]);

    const updateSearchParams = (
        nextPage: number,
        nextPageSize: number,
        overrides?: {
            deviceName?: string;
            targetIp?: string;
            sourceIp?: string;
        }
    ) => {
        const finalDeviceName = overrides?.deviceName ?? deviceName;
        const finalTargetIp = overrides?.targetIp ?? targetIp;
        const finalSourceIp = overrides?.sourceIp ?? sourceIp;

        const params: Record<string, string> = {
            page: String(nextPage),
            pageSize: String(nextPageSize),
        };

        if (finalDeviceName.trim()) params.deviceName = finalDeviceName.trim();
        if (finalTargetIp.trim()) params.targetIp = finalTargetIp.trim();
        if (finalSourceIp.trim()) params.sourceIp = finalSourceIp.trim();

        setSearchParams(params);
    };

    const handleSearch = () => {
        updateSearchParams(1, pageSize);
    };

    const handleReset = () => {
        setDeviceName('');
        setTargetIp('');
        setSourceIp('');
        setSearchParams({
            page: String(DEFAULT_PAGE),
            pageSize: String(DEFAULT_PAGE_SIZE),
        });
    };

    const handlePageChange = (nextPage: number) => {
        if (nextPage < 1 || nextPage > totalPages) return;
        updateSearchParams(nextPage, pageSize);
    };

    const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const nextPageSize = Number(e.target.value || DEFAULT_PAGE_SIZE);
        updateSearchParams(1, nextPageSize);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setSelectedFile(file);
        setUploadError('');
        setUploadSuccess('');
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setUploadError('Please select a file first.');
            setUploadSuccess('');
            return;
        }

        try {
            setUploading(true);
            setUploadError('');
            setUploadSuccess('');

            const result = await uploadSecureCRTPingLog(selectedFile);

            setUploadSuccess(`Upload succeeded. Log ID: ${result?.logId || '-'}`);
            setSelectedFile(null);

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            await loadData();
        } catch (err: any) {
            const status = err?.response?.status;
            const message =
                err?.response?.data?.message ||
                err?.message ||
                'Upload failed.';

            if (status === 409) {
                setUploadError(`Duplicate log file. ${message}`);
            } else {
                setUploadError(message);
            }

            setUploadSuccess('');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (logId: string) => {
        const confirmed = window.confirm(`Are you sure you want to delete ping log ${logId}?`);
        if (!confirmed) return;

        try {
            setDeletingLogId(logId);
            setError('');
            setUploadError('');
            setUploadSuccess('');

            await deletePingLog(logId);

            const isLastItemOnPage = items.length === 1 && page > 1;
            const nextPage = isLastItemOnPage ? page - 1 : page;

            if (nextPage !== page) {
                updateSearchParams(nextPage, pageSize);
            } else {
                await loadData();
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Failed to delete ping log.');
        } finally {
            setDeletingLogId('');
        }
    };

    const handleViewDetail = (logId: string) => {
        navigate(`/pinglog/${logId}`);
    };

    const formatDateTime = (value?: string) => {
        if (!value) return '-';
        return value;
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages: number[] = [];
        const start = Math.max(1, page - 2);
        const end = Math.min(totalPages, page + 2);

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        return (
            <Pagination className="mb-0">
                <Pagination.First disabled={page <= 1} onClick={() => handlePageChange(1)} />
                <Pagination.Prev disabled={page <= 1} onClick={() => handlePageChange(page - 1)} />

                {start > 1 && (
                    <>
                        <Pagination.Item onClick={() => handlePageChange(1)}>1</Pagination.Item>
                        {start > 2 && <Pagination.Ellipsis disabled />}
                    </>
                )}

                {pages.map((p) => (
                    <Pagination.Item key={p} active={p === page} onClick={() => handlePageChange(p)}>
                        {p}
                    </Pagination.Item>
                ))}

                {end < totalPages && (
                    <>
                        {end < totalPages - 1 && <Pagination.Ellipsis disabled />}
                        <Pagination.Item onClick={() => handlePageChange(totalPages)}>
                            {totalPages}
                        </Pagination.Item>
                    </>
                )}

                <Pagination.Next disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)} />
                <Pagination.Last disabled={page >= totalPages} onClick={() => handlePageChange(totalPages)} />
            </Pagination>
        );
    };

    return (
        <div className="mt-3">
            <Card className="mb-3 pinglog-requirement-card">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h5 className="mb-1 pinglog-requirement-title">Ping Log Collection Requirement</h5>
                            <div className="pinglog-requirement-subtitle">
                                Please follow the commands below for all future ping log collection to avoid parsing failures.
                            </div>
                        </div>
                    </div>

                    <div className="mb-4">
                        <div className="pinglog-requirement-section-title">1) Cisco Devices</div>

                        <div className="pinglog-requirement-label mt-3">Use the following format:</div>
                        <div className="pinglog-command-block">
                            ping vrf &lt;vrf_name&gt; &lt;target_ip&gt; source &lt;source_ip&gt; size &lt;packet_size&gt; repeat &lt;repeat_count&gt;
                        </div>

                        <div className="pinglog-requirement-label mt-3">Example:</div>
                        <div className="pinglog-command-block">
                            ping vrf huaweinet 10.196.3.201 source 10.200.1.173 size 1500 repeat 1000
                        </div>

                        <div className="pinglog-requirement-label mt-3">
                            If DF is required, add <code>df</code> at the end:
                        </div>
                        <div className="pinglog-command-block">
                            ping vrf huaweinet 10.196.3.201 source 10.200.1.173 size 1500 repeat 1000 df
                        </div>

                        <div className="pinglog-requirement-label mt-3">After the ping, run:</div>
                        <div className="pinglog-command-block">
                            <div>sho clo</div>
                            <div>or</div>
                            <div>show clock</div>
                        </div>
                    </div>

                    <div>
                        <div className="pinglog-requirement-section-title">2) Juniper Devices</div>

                        <div className="pinglog-requirement-label mt-3">Use the following format:</div>
                        <div className="pinglog-command-block">
                            ping routing-instance &lt;vrf_name&gt; &lt;target_ip&gt; source &lt;source_ip&gt; size &lt;packet_size&gt; count &lt;repeat_count&gt; rapid
                        </div>

                        <div className="pinglog-requirement-label mt-3">Example:</div>
                        <div className="pinglog-command-block">
                            ping routing-instance CUST-VRF-huaweinet 10.196.3.34 source 10.196.3.33 size 1472 count 100 rapid
                        </div>

                        <div className="pinglog-requirement-label mt-3">After the ping, run:</div>
                        <div className="pinglog-command-block">
                            show system uptime | match Current
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-1">Ping Logs</h4>
                    <div className="text-muted">
                        Upload ping logs and view each uploaded record individually.
                    </div>
                </div>
            </div>

            <Card className="pinglog-upload-card mb-3">
                <Card.Body>
                    <div className="pinglog-upload-title mb-3">Upload SecureCRT Ping Log</div>

                    {uploadSuccess && (
                        <Alert variant="success" className="pinglog-alert-success mb-3">
                            {uploadSuccess}
                        </Alert>
                    )}

                    {uploadError && (
                        <Alert variant="danger" className="pinglog-alert-danger mb-3">
                            {uploadError}
                        </Alert>
                    )}

                    <Form.Group>
                        <Form.Label className="pinglog-upload-label">Log File</Form.Label>

                        <div className="pinglog-file-row">
                            <Form.Control
                                type="file"
                                className="pinglog-file-input"
                                accept=".log,.txt"
                                onChange={handleFileChange}
                                ref={fileInputRef}
                            />

                            <Button
                                className="pinglog-upload-btn"
                                onClick={handleUpload}
                                disabled={!selectedFile || uploading}
                            >
                                {uploading ? 'Uploading...' : 'Upload'}
                            </Button>
                        </div>

                        <div className="pinglog-upload-hint">
                            Supported format: .log, .txt
                            {selectedFile ? ` | Selected: ${selectedFile.name}` : ''}
                        </div>
                    </Form.Group>
                </Card.Body>
            </Card>

            <Card className="mb-3 pinglog-dark-card">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Device Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g. bkwi915"
                                    value={deviceName}
                                    onChange={(e) => setDeviceName(e.target.value)}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Target IP</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g. 192.168.200.1"
                                    value={targetIp}
                                    onChange={(e) => setTargetIp(e.target.value)}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Source IP</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g. 10.200.1.173"
                                    value={sourceIp}
                                    onChange={(e) => setSourceIp(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mt-3">
                        <Col className="d-flex gap-2">
                            <Button onClick={handleSearch}>Search</Button>
                            <Button variant="outline-secondary" onClick={handleReset}>
                                Reset
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {error && (
                <Alert variant="danger" className="mb-3">
                    {error}
                </Alert>
            )}

            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div>
                    <strong>Total:</strong> {total}
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="text-muted">Page Size</span>
                    <Form.Select
                        style={{ width: '100px' }}
                        value={pageSize}
                        onChange={handlePageSizeChange}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </Form.Select>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" />
                </div>
            ) : (
                <Table striped bordered hover responsive className="pinglog-table">
                    <thead>
                        <tr>
                            <th>Log ID</th>
                            <th>Device</th>
                            <th>Target IP</th>
                            <th>Source IP</th>
                            <th>Total Rounds</th>
                            <th>Avg PLR</th>
                            <th>Avg RTD</th>
                            <th>Start Time</th>
                            <th>End Time</th>
                            <th>Upload Time</th>
                            <th>Upload User</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr>
                                <td colSpan={13} className="text-center text-muted py-4">
                                    No data
                                </td>
                            </tr>
                        ) : (
                            items.map((item: PingLogListItem) => (
                                <tr key={item.logId}>
                                    <td style={{ minWidth: '170px' }}>{item.logId}</td>
                                    <td>{item.deviceName || '-'}</td>
                                    <td>{item.targetIp || '-'}</td>
                                    <td>{item.sourceIp || '-'}</td>
                                    <td>{item.totalRounds}</td>
                                    <td>{item.avgLossPercent}</td>
                                    <td>{item.avgRttMs ?? '-'}</td>
                                    <td style={{ minWidth: '220px' }}>{item.startClockLine || '-'}</td>
                                    <td style={{ minWidth: '220px' }}>{item.endClockLine || '-'}</td>
                                    <td style={{ minWidth: '180px' }}>{formatDateTime(item.uploadTime)}</td>
                                    <td>{item.userName || '-'}</td>
                                    <td style={{ minWidth: '190px' }}>
                                        <div className="pinglog-action-group">
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                className="pinglog-action-btn"
                                                onClick={() => handleViewDetail(item.logId)}
                                                disabled={deletingLogId === item.logId}
                                            >
                                                View
                                            </Button>

                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                className="pinglog-action-btn pinglog-delete-btn"
                                                onClick={() => handleDelete(item.logId)}
                                                disabled={deletingLogId === item.logId}
                                            >
                                                {deletingLogId === item.logId ? 'Deleting...' : 'Delete'}
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            )}

            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">
                    Page {page} / {totalPages}
                </div>
                <div>{renderPagination()}</div>
            </div>
        </div>
    );
};

export default PingLogListPage;